import React, { useRef, useState } from 'react'
import Map, { Marker, Source, Layer } from 'react-map-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Tooltip,
  IconButton
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

import { transformPolygon, transformPath } from '../../utils/coordinates'
import ConfigurableValues from '../../config/constants'
import { ZONE_COLORS } from '../DeliveryBoundaries/ZoneEditor'
import DrawControl from '../MapDraw/DrawControl'
import { drivingAreaPath } from '../../utils/drivingArea'
import DrivingDistanceField from '../DeliveryBoundaries/DrivingDistanceField'
import { CT, focusRing } from '../../views/cateringTheme'

/**
 * CateringAreaEditor — where a restaurant's own drivers deliver catering
 * (backend migration 132), drawn on its Catering page. The same tools as the
 * Delivery Zones boundary (freehand, or a driving-distance area), but kept
 * apart from it: those areas run regular self-delivery orders and only open
 * with the Self Delivery switch. Controlled — the page saves it with the rest.
 *
 * Props:
 *   areas     [{ name, _path: [{lat,lng}] }]
 *   onChange  (nextAreas) => void
 *   center    { lat, lng } — the restaurant
 *   restaurantName
 *   error     string shown under the list
 */

export const areasFromZones = zones => (zones || []).map(z => ({
  name: z.name,
  _path: z.polygon?.coordinates?.[0] ? transformPolygon(z.polygon.coordinates[0]) : []
}))

export const areasToZoneInput = areas => (areas || [])
  .filter(a => a._path && a._path.length >= 3)
  .map((a, i) => ({ name: (a.name || '').trim() || `Area ${i + 1}`, polygon: { coordinates: transformPath(a._path) }, sortOrder: i }))

// What decides "unsaved": names and shapes, rounded so a reload compares equal.
export const areasSnapshot = areas => JSON.stringify((areas || []).map(a => ({
  name: (a.name || '').trim(),
  path: (a._path || []).map(p => [Math.round(p.lat * 1e6), Math.round(p.lng * 1e6)])
})))

const MAP_STYLE = { width: '100%', height: '100%' }

const DRAW_STYLES = [
  { id: 'gl-draw-polygon-fill', type: 'fill', filter: ['all', ['==', '$type', 'Polygon']], paint: { 'fill-color': '#6366f1', 'fill-opacity': 0.25 } },
  { id: 'gl-draw-polygon-stroke', type: 'line', filter: ['all', ['==', '$type', 'Polygon']], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#4f46e5', 'line-width': 3 } },
  { id: 'gl-draw-line', type: 'line', filter: ['all', ['==', '$type', 'LineString']], layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': '#4f46e5', 'line-width': 3 } },
  { id: 'gl-draw-vertex-halo', type: 'circle', filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']], paint: { 'circle-radius': 7, 'circle-color': '#fff' } },
  { id: 'gl-draw-vertex', type: 'circle', filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point']], paint: { 'circle-radius': 5, 'circle-color': '#4f46e5' } }
]

function pathToFeature(path) {
  if (!path || path.length < 3) return null
  const ring = path.map(p => [p.lng, p.lat])
  ring.push([path[0].lng, path[0].lat])
  return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [ring] } }
}
function featureToPath(feature) {
  const ring = feature?.geometry?.coordinates?.[0]
  if (!ring || ring.length < 4) return []
  return ring.slice(0, -1).map(([lng, lat]) => ({ lat, lng }))
}

const GHOST_BTN = {
  width: 44, height: 44, borderRadius: '10px', flexShrink: 0, color: CT.secondary, bgcolor: 'transparent',
  '& .MuiSvgIcon-root': { fontSize: 20 }
}
const DASHED_BTN = {
  height: 44, borderRadius: '10px', px: 2, bgcolor: CT.card, color: CT.text, fontWeight: 700,
  border: `1px dashed ${CT.dashed}`,
  '&:hover': { bgcolor: CT.page, border: `1px dashed ${CT.text}` }
}
const AREA_NAME_INPUT = {
  flex: 1, minWidth: 0,
  '& .MuiInputBase-root': { fontSize: 15, color: CT.text, borderRadius: '2px' },
  '& .MuiInputBase-root.Mui-focused': focusRing,
  '& input': { fontWeight: 700, py: '4px' },
  '& .MuiInput-root:before': { borderBottom: `1.5px solid ${CT.underline}` },
  '& .MuiInput-root:hover:not(.Mui-disabled):before': { borderBottom: `1.5px solid ${CT.secondary}` },
  '& .MuiInput-root:after': { borderBottom: `1.5px solid ${CT.text}` }
}

export default function CateringAreaEditor({ areas, onChange, center, restaurantName, error }) {
  const { MAPBOX_TOKEN } = ConfigurableValues()
  const [selected, setSelected] = useState(areas.length ? 0 : null)
  const [drawActive, setDrawActive] = useState(false)
  const [modal, setModal] = useState(null) // { mode: 'add'|'edit', method, miles, error, busy }
  const drawRef = useRef(null)
  // The draw control binds its callbacks once, when drawing starts, so they
  // read the areas, the area being drawn and onChange through refs.
  const latest = useRef(areas)
  latest.current = areas
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const editingRef = useRef(null) // index being drawn, or null for a new area
  const setEditing = index => { editingRef.current = index }

  const nextName = len => `Area ${len + 1}`

  const setPath = (index, path) => {
    const list = latest.current
    if (index != null && list[index]) {
      const next = list.map((a, i) => (i === index ? { ...a, _path: path } : a))
      latest.current = next
      onChangeRef.current(next)
      setSelected(index)
      return index
    }
    const next = [...list, { name: nextName(list.length), _path: path }]
    latest.current = next
    onChangeRef.current(next)
    setSelected(list.length)
    return list.length
  }

  const openModal = (mode, index = null) => {
    setDrawActive(false)
    setEditing(index)
    if (index != null) setSelected(index)
    setModal({ mode, method: 'draw', miles: '5', error: '', busy: false })
  }

  const continueModal = async () => {
    if (modal.method === 'draw') {
      setModal(null)
      setDrawActive(true)
      return
    }
    // Driving distance: what a driver can actually reach by road (Mapbox).
    setModal(m => ({ ...m, busy: true, error: '' }))
    try {
      const path = await drivingAreaPath(center, modal.miles, MAPBOX_TOKEN)
      setPath(editingRef.current, path)
      setModal(null)
      setEditing(null)
    } catch (e) {
      setModal(m => ({ ...m, busy: false, error: e.message }))
    }
  }

  const handleDrawReady = draw => {
    drawRef.current = draw
    if (!draw || (draw.getAll()?.features?.length || 0) > 0) return
    const editing = editingRef.current
    const existing = editing != null ? latest.current[editing]?._path : null
    if (existing && existing.length >= 3) {
      const [id] = draw.add(pathToFeature(existing))
      draw.changeMode('direct_select', { featureId: id })
    } else {
      draw.changeMode('draw_polygon')
    }
  }

  const drawnPath = () => {
    const feature = drawRef.current?.getAll()?.features?.[0]
    return feature ? featureToPath(feature) : []
  }

  // A shape finished or reshaped: keep it on the area being drawn.
  const syncDraw = () => {
    const path = drawnPath()
    if (path.length < 3) return
    const index = setPath(editingRef.current, path)
    if (editingRef.current == null) setEditing(index)
  }

  const finishDrawing = () => {
    const path = drawnPath()
    if (path.length >= 3) setPath(editingRef.current, path)
    setDrawActive(false)
    setEditing(null)
  }

  const removeArea = index => {
    onChange(areas.filter((_, i) => i !== index))
    setSelected(s => (s === null || s === index ? null : s > index ? s - 1 : s))
  }

  return (
    <Box sx={{ containerType: 'inline-size' }}>
      <Box sx={{
        display: 'flex', flexDirection: 'column', gap: 3,
        '@container (min-width: 760px)': { flexDirection: 'row', height: 480 }
      }}>
        {/* Area list */}
        <Box sx={{
          display: 'flex', flexDirection: 'column', minWidth: 0,
          '@container (min-width: 760px)': { width: 300, flexShrink: 0 }
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: CT.text }}>Catering areas ({areas.length})</Typography>
            <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => openModal('add')} variant="outlined" sx={DASHED_BTN}>
              Add area
            </Button>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1, p: '2px', m: '-2px' }}>
            {areas.length === 0 && (
              <Typography variant="body2" sx={{ fontSize: 14, color: CT.secondary, p: '16px 18px', borderRadius: '12px', border: `1px dashed ${CT.inputBorder}` }}>
                No catering area yet. Add one (draw it, or a driving distance) to set exactly where your drivers deliver catering.
              </Typography>
            )}
            {areas.map((area, i) => {
              const color = ZONE_COLORS[i % ZONE_COLORS.length]
              const isSel = selected === i
              return (
                <Box key={i} onClick={() => setSelected(i)} sx={{
                  display: 'flex', alignItems: 'center', gap: 1, minHeight: 56, pl: '14px', pr: '6px', flexShrink: 0,
                  borderRadius: '12px', cursor: 'pointer', boxSizing: 'border-box',
                  border: `1px solid ${isSel ? color : CT.border}`,
                  boxShadow: isSel ? `inset 0 0 0 1px ${color}` : 'none',
                  backgroundColor: isSel ? `${color}14` : CT.card,
                  transition: 'border-color 150ms ease, background-color 150ms ease',
                  '&:hover': { borderColor: isSel ? color : '#C4CAD4' }
                }}>
                  <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                  {isSel ? (
                    <TextField value={area.name} onChange={e => onChange(areas.map((a, j) => (j === i ? { ...a, name: e.target.value } : a)))}
                      onClick={e => e.stopPropagation()} size="small" variant="standard" placeholder="Area name"
                      inputProps={{ maxLength: 100, 'aria-label': 'Area name' }} sx={AREA_NAME_INPUT} />
                  ) : (
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, color: CT.text, overflowWrap: 'anywhere' }}>{area.name}</Typography>
                  )}
                  <Tooltip title="Redraw / edit shape">
                    <IconButton size="small" aria-label={`Edit ${area.name}`} onClick={e => { e.stopPropagation(); openModal('edit', i) }}
                      sx={{ ...GHOST_BTN, '&:hover': { bgcolor: CT.ghostHover, color: CT.text } }}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete area">
                    <IconButton size="small" aria-label={`Delete ${area.name}`} onClick={e => { e.stopPropagation(); removeArea(i) }}
                      sx={{ ...GHOST_BTN, '&:hover': { bgcolor: CT.ghostHover, color: CT.danger } }}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )
            })}
          </Box>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Box>

        {/* Map */}
        <Box sx={{
          flex: 'none', minWidth: 0, height: 380, position: 'relative',
          borderRadius: '14px', overflow: 'hidden', border: `1px solid ${CT.frame}`,
          '@container (min-width: 760px)': { flex: 1, height: 'auto' }
        }}>
          {drawActive && (
            <Box sx={{
              position: 'absolute', top: 12, right: 12, zIndex: 2, maxWidth: 'calc(100% - 24px)', boxSizing: 'border-box',
              display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center',
              bgcolor: CT.card, border: `1px solid ${CT.border}`, borderRadius: '12px', pl: 1.75, pr: 0.75, py: 0.75,
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.18)'
            }}>
              <Typography variant="caption" sx={{ fontSize: 13, color: CT.secondary }}>Click &amp; drag to draw · release to close</Typography>
              <Button size="small" variant="contained" onClick={finishDrawing} sx={{ borderRadius: '10px', textTransform: 'none' }}>Done drawing</Button>
            </Box>
          )}
          {center && (
            <Map
              mapboxAccessToken={MAPBOX_TOKEN}
              initialViewState={{ longitude: center.lng, latitude: center.lat, zoom: 10.5 }}
              style={MAP_STYLE}
              mapStyle="mapbox://styles/mapbox/streets-v12"
            >
              {drawActive && (
                <DrawControl position="top-left" freehand styles={DRAW_STYLES} displayControlsDefault={false}
                  controls={{ polygon: true, trash: true }} defaultMode="simple_select"
                  onReady={handleDrawReady} onCreate={syncDraw} onUpdate={syncDraw} onDelete={syncDraw} />
              )}
              <Marker longitude={center.lng} latitude={center.lat} anchor="center">
                <div title={restaurantName} style={{ width: 18, height: 18, borderRadius: '50%', background: '#D32F2F', border: '2px solid #fff', boxShadow: '0 1px 4px rgba(0,0,0,0.4)' }} />
              </Marker>
              {areas.map((area, i) => {
                if (drawActive && i === editingRef.current) return null
                const feature = pathToFeature(area._path)
                if (!feature) return null
                const color = ZONE_COLORS[i % ZONE_COLORS.length]
                const isSel = selected === i
                return (
                  <Source key={i} id={`catering-area-${i}`} type="geojson" data={feature}>
                    <Layer id={`catering-area-fill-${i}`} type="fill" paint={{ 'fill-color': color, 'fill-opacity': isSel ? 0.3 : 0.15 }} />
                    <Layer id={`catering-area-line-${i}`} type="line" paint={{ 'line-color': color, 'line-width': isSel ? 3 : 1.5 }} />
                  </Source>
                )
              })}
            </Map>
          )}
        </Box>
      </Box>

      {/* Add / redraw an area */}
      <Dialog open={!!modal} onClose={() => !modal?.busy && setModal(null)} maxWidth="xs" fullWidth
        sx={{ '& .MuiBackdrop-root': { bgcolor: 'rgba(15, 23, 42, 0.62)' } }}
        PaperProps={{
          sx: {
            borderRadius: '18px', boxShadow: '0 24px 60px rgba(15,23,42,0.35)',
            bgcolor: '#FFFFFF', backgroundImage: 'none', color: CT.text, fontFamily: CT.font,
            m: { xs: 2, sm: 4 }, width: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' }
          }
        }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: 22, lineHeight: 1.25, color: CT.text, p: { xs: '22px 20px 8px', sm: '26px 28px 8px' } }}>
          {modal?.mode === 'edit' ? 'Edit catering area' : 'Add catering area'}
        </DialogTitle>
        <DialogContent sx={{ px: { xs: '20px', sm: '28px' }, pb: 3 }}>
          {modal?.error && <Alert severity="error" sx={{ mb: 2 }}>{modal.error}</Alert>}
          <Typography variant="body2" sx={{ fontSize: 15, color: CT.body }} mb={1}>How do you want to define the area?</Typography>
          <RadioGroup value={modal?.method || 'draw'} onChange={e => { const method = e.target.value; setModal(m => ({ ...m, method, error: '' })) }}>
            <FormControlLabel value="draw" control={<Radio />} label="Draw on map (freehand)" />
            <FormControlLabel value="distance" control={<Radio />} label="Driving distance from restaurant" />
          </RadioGroup>
          {modal?.method === 'draw' && (
            <Typography variant="caption" sx={{ display: 'block', ml: 4, mt: 0.5, fontSize: 13, lineHeight: 1.5, color: CT.secondary, '& strong': { color: CT.text } }}>
              The map switches to draw mode — click &amp; drag to trace the area, then press <strong>Done drawing</strong>.
            </Typography>
          )}
          {modal?.method === 'distance' && (
            <Box sx={{ ml: 4, mt: 1 }}>
              <DrivingDistanceField value={modal.miles} onChange={v => setModal(m => ({ ...m, miles: v, error: '' }))} disabled={modal.busy} />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{
          px: { xs: '20px', sm: '28px' }, py: '16px', gap: '10px', flexWrap: 'wrap',
          borderTop: `1px solid ${CT.border}`, '& > :not(style) ~ :not(style)': { ml: 0 }
        }}>
          <Button onClick={() => setModal(null)} disabled={modal?.busy} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={continueModal} variant="contained" disabled={modal?.busy}
            startIcon={modal?.busy ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, minWidth: 110 }}>
            {modal?.method === 'draw' ? 'Start drawing' : modal?.busy ? 'Measuring roads…' : 'Add area'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
